import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '@/contexts/LanguageContext';

interface NavigationDotsProps {
  activeSection: number;
  onSectionChange: (index: number) => void;
  sectionConfig?: Array<{
    name_ar: string;
    name_en: string;
    theme: 'light' | 'dark';
  }>;
}

// Default section themes and names (fallback for static pages)
const defaultSectionConfig = [
  { name_ar: 'الرئيسية', name_en: 'Home', theme: 'dark' },
  { name_ar: 'من نحن', name_en: 'About', theme: 'light' },
  { name_ar: 'قطاعاتنا', name_en: 'Sectors', theme: 'dark' },
  { name_ar: 'رؤيتنا', name_en: 'Vision', theme: 'light' },
  { name_ar: 'تواصل معنا', name_en: 'Contact', theme: 'dark' },
];

const NavigationDots = ({ activeSection, onSectionChange, sectionConfig }: NavigationDotsProps) => {
  const { language } = useLanguage();
  const sections = sectionConfig || defaultSectionConfig;
  const currentSectionTheme = sections[activeSection]?.theme || 'light';
  const isDarkSection = currentSectionTheme === 'dark';
  const isRTL = language === 'ar';
  
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (sections.length === 0) return;
    const go = (idx: number) => onSectionChange(Math.max(0, Math.min(sections.length - 1, idx)));
    if (e.key === 'ArrowUp') { e.preventDefault(); go(activeSection - 1); }
    if (e.key === 'ArrowDown') { e.preventDefault(); go(activeSection + 1); }
    if (e.key === 'Home') { e.preventDefault(); go(0); }
    if (e.key === 'End') { e.preventDefault(); go(sections.length - 1); }
  };

  const prefersReducedMotion = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  return (
  <div
      className={`hidden 2xl:block fixed ${isRTL ? 'left-10 2xl:left-20' : 'right-10 2xl:right-20'} top-1/2 transform -translate-y-1/2 z-40 space-y-4 select-none`}
      role="navigation"
      aria-label={language === 'ar' ? 'التنقل بين أقسام الصفحة' : 'Page section navigation'}
      aria-orientation="vertical"
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
  {sections.map((section, index) => (
        <motion.div
          key={index}
          className="relative group pointer-events-auto"
          whileHover={prefersReducedMotion ? undefined : { scale: 1.2 }}
          whileTap={prefersReducedMotion ? undefined : { scale: 0.9 }}
        >
          {/** Associate each control with its section for better a11y */}
          {/** SectionsRuntime assigns ids like `section-${index}` */}
          {/** We reference the same pattern here */}
          {/** Note: if a section id is missing, aria-controls is harmless */}
          <button
            type="button"
            onClick={() => onSectionChange(index)}
            aria-label={language === 'ar' ? section.name_ar : section.name_en}
            aria-current={activeSection === index ? 'true' : undefined}
            aria-controls={`section-${index}`}
            className={`p-2 -m-2 rounded-full transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary/60 focus:ring-offset-transparent`}
          />
          {/** Visual dot inside the larger hit area */}
          <span
            aria-hidden="true"
            className={`pointer-events-none absolute inset-0 flex items-center justify-center`}
          >
            <span
              className={`w-3 h-3 rounded-full border-2 ${
                activeSection === index
                  ? isDarkSection
                    ? 'bg-white border-white'
                    : 'bg-primary border-primary'
                  : isDarkSection
                    ? 'bg-transparent border-white/50 group-hover:border-white'
                    : 'bg-transparent border-primary/50 group-hover:border-primary'
              }`}
            />
          </span>
          
          {/* Tooltip (RTL-aware positioning) */}
          <div className={`absolute ${isRTL ? 'left-full ml-3 lg:ml-4' : 'right-full mr-3 lg:mr-4'} top-1/2 transform -translate-y-1/2 px-3 py-1 rounded-md text-sm font-medium opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-all duration-300 pointer-events-none whitespace-nowrap ${
            isDarkSection 
              ? 'bg-white text-primary' 
              : 'bg-primary text-white'
          }`}>
            {language === 'ar' ? section.name_ar : section.name_en}
          </div>
        </motion.div>
      ))}
    </div>
  );
};

export default NavigationDots;